import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import SSRIcon from '@ingka/ssr-icon'
import arrowLeft from '@ingka/ssr-icon/paths/arrow-left'
import cross from '@ingka/ssr-icon/paths/cross'
import { useTranslation } from 'react-i18next'
import { func, bool, string, arrayOf } from 'prop-types'
import Icon from 'components/uiElements/Icon'
import generateThumbnailFromPreview from 'utils/generateThumbnailFromPreview'

import { compositionType, productsDataType } from 'models/propTypes'
import FavoriteButtonContainer from 'containers/uiElements/FavoriteButtonContainer'
import { TRACK_LOCATION_DESIGN_SUMMARY } from 'models/tracking/constants'
import { ROTATE_PHONE } from 'toasts/toastTypes.js'
import Button from '@ingka/button'
import { ORIENTATION_LANDSCAPE } from 'models/ui/constants'
import ProductItemContainer from 'containers/designSidepanel/ProductItemContainer'

require('./DesignSummaryModalMobile.scss')

const DesignSummaryModalMobile = ({
    closeModal,
    addRoomToBag,
    hasAddedToBag,
    composition,
    totalPrice,
    localItemNumbers,
    showToast,
    hideToast,
    orientation,
    onCloseMagicWindow,
    openExitPopup,
    addedToRoomItems,
}) => {
    const panoImageRef = useRef(null)

    useEffect(() => {
        // Since outlines are disabled on mobile; the thumbnail
        // is generated directly instead of using the renderer's roomScreenshot API
        generateThumbnailFromPreview().then((thumb) => {
            const imageElement = panoImageRef && panoImageRef.current

            if (imageElement) {
                imageElement.src = thumb.thumbDataUrl
            }
        })
    }, [])

    useEffect(() => {
        if (orientation === ORIENTATION_LANDSCAPE) {
            showToast(ROTATE_PHONE)
        } else {
            hideToast(ROTATE_PHONE)
        }
    }, [orientation])

    const { t } = useTranslation()
    const compositionName = composition && (composition.name || t('design.default composition name'))
    const hasLandscapeOrientation = orientation === ORIENTATION_LANDSCAPE

    const onClose = () => {
        if (!hasAddedToBag) {
            onCloseMagicWindow()
        } else {
            openExitPopup()
        }
    }

    const onAddRoomToBagClick = () => {
        addRoomToBag()
        openExitPopup()
    }
    const hasProductsInRoom = addedToRoomItems && addedToRoomItems.length
    const hasItemsToFavorite = localItemNumbers && localItemNumbers.length

    return (
        <div
            className={classNames('DesignSummaryModalMobile', {
                landscape: hasLandscapeOrientation,
            })}
        >
            <div className="modalHeader">
                <Icon svg code="logo_ikea" className="logoHome" />
                <button onClick={onClose}>
                    <SSRIcon paths={cross} />
                </button>
            </div>

            <div className="modalBody">
                <div className="modalBodyHeader">
                    <button onClick={closeModal} className="backButton">
                        <SSRIcon paths={arrowLeft} />
                    </button>
                    <div className="compositionName">{compositionName}</div>
                </div>
                <div className="modalCompositionImage">
                    <img src="" alt="" ref={panoImageRef} />
                </div>
                <div className="shopDesign">{t('designSummary.shop this design')}</div>
                <div className="totalPrice centerSpacedItems">
                    <div className="priceText">{t('designSummary.total price')}</div>
                    <div className="priceValue">{totalPrice}</div>
                </div>
                <div className="ctas">
                    <Button
                        onClick={onAddRoomToBagClick}
                        type="emphasised"
                        text={t('design.add room to shopping bag')}
                    />
                    {hasItemsToFavorite && (
                        <div className="favoriteAllCta">
                            <FavoriteButtonContainer
                                localItemNumbers={localItemNumbers}
                                location={TRACK_LOCATION_DESIGN_SUMMARY}
                            />
                        </div>
                    )}
                </div>

                {hasProductsInRoom && (
                    <div className="productsInRoom">
                        {addedToRoomItems.map((productData) => (
                            <ProductItemContainer
                                furnitureData={productData}
                                key={productData.uniqueId || productData.id}
                                isInDesignSummaryView
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

DesignSummaryModalMobile.defaultProps = {
    localItemNumbers: null,
}

DesignSummaryModalMobile.propTypes = {
    closeModal: func.isRequired,
    composition: compositionType.isRequired,
    addRoomToBag: func.isRequired,
    hasAddedToBag: bool.isRequired,
    totalPrice: string.isRequired,
    localItemNumbers: arrayOf(string),
    showToast: func.isRequired,
    hideToast: func.isRequired,
    orientation: string.isRequired,
    onCloseMagicWindow: func.isRequired,
    openExitPopup: func.isRequired,
    addedToRoomItems: productsDataType.isRequired,
}

export default DesignSummaryModalMobile
